import React, { Ref, useEffect, useState } from 'react';
import { always, both, find, flip, includes, is, pipe, propEq, toLower, trim, values, equals, ifElse } from 'ramda';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import NextLink from 'next/link';

import pintrk, { PINTEREST_EVENTS } from '@utils/pinterestTag';
import { FavoriteButton } from '@components/favoriteButton';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { INCHES_TO_CM_CONVERTER } from '@modules/ui';
import { ARTWORK_BAG_STATUS } from '@components/PDP/artworkSidebar/artworkSidebar.constants';
import { WaitlistButton } from '@components/waitlistButton';
import { BUTTON_SIZE } from '@components/form';
import { useAddShoppingCartItem, useGetShoppingCart } from '@hooks/useShoppingCart';
import { useHeaderSublevel, useMeasure } from '@hooks/useUi';
import { SUBLEVEL } from '@components/header/header.constants';
import reportError from '@utils/reportError';
import { THIRD_DIMS_ARTWORKS } from '@components/PDP/pdp.constants';
import { AddToBagWithQuantitySelect } from '@components/addToBagWithQuantitySelect';
import { getFullArtistName, getMaximumUnitsToPurchase, getShowAddToBag } from '@utils/helpers';
import { AddToBag } from '@components/addToBag';
import { Artwork, ArtworkVariant } from '@definitions/artworks.types';
import { MAXIMUM_UNITS_PER_CART, ROUTE } from '@utils/constants';
import { useTrackConversions } from '@hooks/useTrackConversions';
import { useSellingState, useWaitlistButtonLabel } from '@hooks/useArtworks/useArtworks';
import { CONVERSIONS_CONTENT_TYPE, CONVERSIONS_EVENTS } from '@definitions/conversions.types';
import { PhysicalItem } from '@definitions/shoppingCart.types';

import { ArtworkVariants } from '../artworkVariants';
import { arrayRange } from '../artworkDetails/quantitySelect/quantitySelect.utils';

import {
  Container,
  Artist,
  DimensionButton,
  FavoriteButtonContainer,
  Details,
  DetailsRow,
  Title,
  ButtonsContainer,
  ActionsContainer,
  TopRow,
  BottomRow,
} from './artworkDetailsMobile.styles';

export interface ArtworkDetailsMobileProps {
  artwork: Artwork;
  defaultVariant: ArtworkVariant | null;
  innerRef?: Ref<HTMLDivElement>;
  showBackground?: boolean;
}

export const ArtworkDetailsMobile = ({
  artwork: {
    wysiwygTitle,
    title,
    unframedWidth,
    unframedHeight,
    unframedDepth,
    slug,
    price,
    favorite,
    bcId,
    medium,
    year,
    artist,
    addToWaitlist,
    inventoryLevel,
    liveStartAt,
    liveEndAt,
    waitlist,
    subcategory,
    variants,
    isTrade,
    maximumUnitsPerCart = MAXIMUM_UNITS_PER_CART,
  },
  artwork,
  innerRef,
  defaultVariant,
  showBackground = false,
}: ArtworkDetailsMobileProps) => {
  const { lineItems } = useGetShoppingCart() || {};
  const { addCartItem, cart } = useAddShoppingCartItem();
  const { openHeaderSublevel } = useHeaderSublevel();
  const [variant, setVariant] = useState<ArtworkVariant | null>(defaultVariant);
  const intl = useIntl();
  const router = useRouter();
  const { sendConversion } = useTrackConversions();
  const { isInches, toggleMeasure } = useMeasure();
  const [isExclusive] = useState(Boolean(router.query.exclusive));
  const [isShoppable] = useState(router.query.isShoppable === 'true');
  const [bagStatus, setBagStatus] = useState<ARTWORK_BAG_STATUS>(ARTWORK_BAG_STATUS.IDLE);
  const isAddedToBag = bagStatus === ARTWORK_BAG_STATUS.ADDED;
  const { isPostSellingDate, isBeforeSellingDate } = useSellingState(artwork);
  const maximumUnitsToPurchase = getMaximumUnitsToPurchase(maximumUnitsPerCart);
  const isSingularItem = maximumUnitsToPurchase <= 1;
  const label = useWaitlistButtonLabel({
    isShoppable: !!isShoppable,
    isExclusive: isExclusive,
    isPdp: true,
    isPostSellingDate,
  });

  const quantityInCartOfTheSameProduct = lineItems?.physicalItems
    ?.filter(({ productId, variantId }) => {
      if (variant) return variantId === Number(variant?.bigCommerceId);
      return productId === Number(bcId);
    })
    ?.reduce((accumulator: number, current: PhysicalItem) => accumulator + current.quantity, 0);

  const quantitySelectOptions = arrayRange(
    1,
    Math.min(inventoryLevel, maximumUnitsPerCart) - quantityInCartOfTheSameProduct
  );

  useEffect(() => {
    const isInCart = ifElse(
      equals(null),
      always(!!find(propEq('productId', Number(bcId)), cart.lineItems.physicalItems)),
      always(
        !!find(
          both(propEq('productId', Number(bcId)), propEq('variantId', Number(variant?.bigCommerceId))),
          cart.lineItems.physicalItems
        )
      )
    )(variant);

    if (isSingularItem) {
      setBagStatus(isInCart ? ARTWORK_BAG_STATUS.ADDED : ARTWORK_BAG_STATUS.NOT_ADDED);
    } else {
      if (bagStatus === ARTWORK_BAG_STATUS.IDLE || quantitySelectOptions.length > 0) {
        setBagStatus(ARTWORK_BAG_STATUS.NOT_ADDED);
      }
      if (quantitySelectOptions.length === 0) {
        setBagStatus(ARTWORK_BAG_STATUS.ADDED);
      }
    }
  }, [bcId, variant, cart.lineItems]);

  const handleAddItem = async (quantity: number) => {
    const currentQuantity = quantityInCartOfTheSameProduct + quantity;

    if (currentQuantity > maximumUnitsToPurchase) {
      return openHeaderSublevel({ sublevel: SUBLEVEL.BAG });
    }

    if (!isAddedToBag) {
      setBagStatus(ARTWORK_BAG_STATUS.LOADING);
      try {
        await addCartItem({
          physicalItem: { productId: Number(bcId), variantId: variant?.bigCommerceId, inventoryLevel, quantity },
        });

        openHeaderSublevel({ sublevel: SUBLEVEL.BAG });
        await sendConversion({
          eventType: CONVERSIONS_EVENTS.ADD_TO_CART,
          contentType: CONVERSIONS_CONTENT_TYPE.PRODUCT,
          contents: [
            {
              title,
              quantity: currentQuantity,
              price,
              id: bcId,
            },
          ],
          currency: cart.currency.code,
        });
        pintrk.event(PINTEREST_EVENTS.ADD_TO_CART, {
          value: price,
          order_quantity: currentQuantity,
          currency: cart.currency.code,
          product_id: bcId,
        });
      } catch (e) {
        reportError(e);
        setBagStatus(ARTWORK_BAG_STATUS.NOT_ADDED);
      }
    } else {
      openHeaderSublevel({ sublevel: SUBLEVEL.BAG });
    }
  };

  const inchesCopy = intl.formatMessage({ id: 'artworkSidebar.inches', defaultMessage: 'inches' });
  const centimeterCopy = intl.formatMessage({ id: 'artworkSidebar.cm', defaultMessage: 'cm' });
  const roundNumber = (number: number) => (is(Number, number) ? number.toFixed(1) : Number(number).toFixed(1));
  // @ts-ignore
  const isDepthCategory = pipe(trim, toLower, flip(includes)(values(THIRD_DIMS_ARTWORKS)))(subcategory);
  const getThirdDimension = (depth?: number) => (depth && isDepthCategory ? ` × ${roundNumber(depth)}` : '');
  const getDimensionsCopy = (width: number, height: number, depth?: number) =>
    `${roundNumber(width)} × ${roundNumber(height)}${getThirdDimension(depth)}`;

  const showAddToBag = getShowAddToBag({
    inventoryLevel,
    liveStartAt,
    liveEndAt,
    addToWaitlist,
    isExclusive,
    isShoppable,
    isTrade,
  });

  const renderAddToBagButton = renderWhenTrue(() => {
    if (maximumUnitsToPurchase > 1)
      return (
        <AddToBagWithQuantitySelect
          isVariant={!!defaultVariant}
          onClick={handleAddItem}
          status={bagStatus}
          price={price}
          inventoryLevel={inventoryLevel}
          options={quantitySelectOptions}
        />
      );
    return <AddToBag onClick={handleAddItem} status={bagStatus} price={price} inventoryLevel={inventoryLevel} />;
  });

  const renderSize = renderWhenTrueOtherwise(
    always(`${getDimensionsCopy(unframedHeight, unframedWidth, unframedDepth)} ${inchesCopy} / `),
    always(
      `${getDimensionsCopy(
        unframedHeight * INCHES_TO_CM_CONVERTER,
        unframedWidth * INCHES_TO_CM_CONVERTER,
        unframedDepth * INCHES_TO_CM_CONVERTER
      )} ${centimeterCopy} / `
    )
  );
  const renderDimensionsCopy = renderWhenTrueOtherwise(always(centimeterCopy), always(inchesCopy));
  const renderCTA = renderWhenTrueOtherwise(
    always(renderAddToBagButton(bagStatus !== ARTWORK_BAG_STATUS.IDLE)),
    always(
      <WaitlistButton
        isPrelaunchable={isBeforeSellingDate && isExclusive && !isShoppable}
        shouldOverridePrelaunchableCopy={addToWaitlist}
        slug={slug}
        waitlist={waitlist}
        buttonSize={BUTTON_SIZE.M}
        fullWidth
        price={price}
        bcId={bcId}
        title={title}
        label={label}
        isPostSellingDate={isPostSellingDate}
      />
    )
  );

  const getTitleAndYear = () => {
    const artworkTitle = wysiwygTitle ? wysiwygTitle.replace(/<\/?p[^>]*>/g, '') : title;

    return `<span>${artworkTitle}, <span class='no-italic'>${year}</span></span>`;
  };

  const renderVariants = renderWhenTrue(
    always(
      <ArtworkVariants
        variants={variants}
        setVariant={setVariant}
        variant={variant || variants[0]}
        artworkSlug={slug}
        artistSlug={artist.slug}
      />
    )
  );

  const renderComingSoon = renderWhenTrue(always(<DetailsRow isSecondary>Coming soon</DetailsRow>));

  return (
    <Container ref={innerRef} showBackground={showBackground}>
      <TopRow showBackground={showBackground}>
        <Details>
          <a
            onClick={() => {
              window.history.pushState({}, '', `${ROUTE.ARTISTS}/${artist.slug}`);
            }}
            href={`${ROUTE.ARTISTS}/${artist.slug}`}
          >
            <Artist>{getFullArtistName(artist)}:</Artist>
          </a>
          <DetailsRow>
            <Title dangerouslySetInnerHTML={{ __html: getTitleAndYear() }} italic={!wysiwygTitle} />
          </DetailsRow>
          <DetailsRow>{medium}</DetailsRow>
          <DetailsRow>
            {renderSize(isInches)}
            <DimensionButton onClick={() => toggleMeasure()}>{renderDimensionsCopy(isInches)}</DimensionButton>
          </DetailsRow>
          {renderComingSoon(isExclusive && !isShoppable)}
        </Details>

        <ActionsContainer>
          <ButtonsContainer>
            <FavoriteButtonContainer>
              <FavoriteButton slug={slug} favorite={favorite} width={32} height={32} />
            </FavoriteButtonContainer>
          </ButtonsContainer>
        </ActionsContainer>
      </TopRow>

      <BottomRow>
        {renderVariants(variants.length > 1)}
        {renderCTA(showAddToBag)}
      </BottomRow>
    </Container>
  );
};
